import { useMemo, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm, useFormContext } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDebouncedCallback } from 'use-debounce'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'
import { useCreateConsultationMutation } from '@/core/services/consultation'
import { useRemovePatientFromQueueMutation } from '@/core/services/patient-queue'
import { useGetConsultationReasonsQuery } from '@/core/services/consultation'
import { useGetSpecialityOptionsQuery } from '@/core/services/speciality-option'
import { ConsultationStatus, ConsultationType, CreateConsultationDto } from '@/core/models/consultation.model'

const schema = z
  .object({
    type: z.nativeEnum(ConsultationType),
    waitingRoomId: z.string().optional(),
    speciality: z.string().optional(),
    reason: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.type === ConsultationType.Classic) {
        return !!data.waitingRoomId
      }
      return true
    },
    {
      message: 'Waiting room is required for classic consultation',
      path: ['waitingRoomId'],
    }
  )

type PreConsultationFormData = z.infer<typeof schema>

type UsePreConsultationFormParams = {
  open: boolean
  patientId?: string
  onSelect: (waitingRoomId: string | null) => void
}

export function usePreConsultationForm({ open, patientId, onSelect }: UsePreConsultationFormParams) {
  const navigate = useNavigate()

  // Mutations
  const [removePatientFromQueue] = useRemovePatientFromQueueMutation()
  const [createConsultation, { isLoading: isCreating }] = useCreateConsultationMutation()

  // Form setup
  const rhForm = useForm<PreConsultationFormData>({
    defaultValues: {
      waitingRoomId: '',
      reason: '',
      speciality: '',
      type: ConsultationType.Classic,
    },
    resolver: zodResolver(schema),
  })
  const { reset } = rhForm

  const typeValue = rhForm.watch('type')

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  const handleSubmit = async (data: PreConsultationFormData) => {
    if (!patientId) return

    try {
      switch (data.type) {
        case ConsultationType.Emergency:
          await removePatientFromQueue(patientId).unwrap()
          navigate(`/emergency`, { state: { patientId }, replace: true })
          return
        case ConsultationType.Local: {
          await removePatientFromQueue(patientId).unwrap()
          const consultationDto: CreateConsultationDto = {
            patient: patientId,
            type: data.type,
            status: ConsultationStatus.Active,
            startDate: new Date().toISOString(),
            speciality: data.speciality,
            reason: data.reason,
          }
          const consultation = await createConsultation(consultationDto).unwrap()
          navigate(`/consultations/${consultation._id}/local`, { replace: true })
          return
        }
        default: {
          await removePatientFromQueue(patientId).unwrap()
          const dto: CreateConsultationDto = {
            patient: patientId,
            startDate: new Date().toISOString(),
            status: ConsultationStatus.Scheduled,
            type: data.type,
            waitingRoom: data.waitingRoomId,
            reason: data.reason,
            speciality: data.speciality,
          }
          const consultation = await createConsultation(dto).unwrap()
          navigate(`/consultations/${consultation._id}/live`, { replace: true })
          return
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    rhForm,
    typeValue,
    handleSubmit,
    isCreating,
  }
}

export function usePreConsultationFormContext() {
  return useFormContext<PreConsultationFormData>()
}

export function useReasonOptions() {
  const { data: consultationReasons = [] } = useGetConsultationReasonsQuery({})

  return useMemo(() => consultationReasons.map((r) => r.name), [consultationReasons])
}

export function useSpecialityField() {
  const [searchText, setSearchText] = useState('')

  const { data: specialityOptions } = useGetSpecialityOptionsQuery({ smartSearch: searchText })

  const searchSpeciality = useDebouncedCallback((value: string) => {
    setSearchText(value || '')
  }, 300)

  const options = useMemo(() => {
    return specialityOptions?.map((option) => ({ label: option.name, value: option.name })) ?? []
  }, [specialityOptions])

  return {
    options,
    searchSpeciality,
  }
}

export function useWaitingRoomOptions() {
  const authUser = useAppSelector(selectAuthUser)
  const { data: waitingRoomsResponse } = useListWaitingRoomsQuery({ query: {}, options: { limit: 0, skip: 0 } }, { skip: !authUser })

  return useMemo(() => {
    const allWaitingRooms = waitingRoomsResponse?.items ?? []
    const userEstablishmentId = typeof authUser?.establishment === 'string' ? authUser?.establishment : authUser?.establishment?._id

    const filteredWaitingRooms = userEstablishmentId
      ? allWaitingRooms.filter((waitingRoom) => {
          const establishmentIds = (waitingRoom.establishments || []).map((establishment: any) =>
            typeof establishment === 'string' ? establishment : establishment?._id
          )
          return establishmentIds.includes(userEstablishmentId)
        })
      : allWaitingRooms

    return filteredWaitingRooms.map((waitingRoom) => ({
      label: waitingRoom.name,
      value: waitingRoom._id,
    }))
  }, [waitingRoomsResponse, authUser])
}
